'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
var _toConsumableArray = require('@babel/runtime/helpers/toConsumableArray');
var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var React = require('react');
var react = require('@chakra-ui/react');
function _interopDefaultLegacy(e) {
  return e && typeof e === 'object' && 'default' in e ? e : {
    'default': e
  };
}
var _toConsumableArray__default = /*#__PURE__*/_interopDefaultLegacy(_toConsumableArray);
var _slicedToArray__default = /*#__PURE__*/_interopDefaultLegacy(_slicedToArray);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var DEFAULT_TYPE_MAP = {
  next: 'nächsten',
  prev: 'vorherigen',
  first: 'ersten',
  last: 'letzten'
};
var defaultGetAriaLabel = function defaultGetAriaLabel(page, type, isActive) {
  if (type === 'page') {
    if (isActive) {
      return "Seite ".concat(page);
    }
    return "Zu Seite ".concat(page);
  }
  return "Zur ".concat(DEFAULT_TYPE_MAP[type], " Seite");
};
var defaultRenderItem = function defaultRenderItem(Component, props, page) {
  return /*#__PURE__*/React__default["default"].createElement(Component, props, page);
};

// https://dev.to/namirsab/comment/2050
var range = function range(start, end) {
  var length = end - start + 1;
  return Array.from({
    length: length
  }, function (_, i) {
    return start + i;
  });
};

/**
 * Handles the count
 *
 * Makes sure that a count...
 *   * below 1, e.g. 0 or 0.8, is set to 1
 *   * between 1 and 2, e.g. 1.7, is set to 2
 *   * any other count is rounded down, e.g. 6.3 should be 6 pages.
 *
 * The reason for those checks is that then, the consumer can simply pass in
 * `items / limit` (which might return a float) and not care about handling
 *  the rounding.
 */
var handleCount = function handleCount(count) {
  if (count <= 1) return 1;
  if (count <= 2) return 2;
  return Math.floor(count);
};

/**
 * Handles the page value
 *
 * Makes sure that the page never goes below 1.
 * The reason for those checks is the same as the `count` checks.
 */
var handlePage = function handlePage(page, count) {
  // Do return it as if is not a number (i.e. undefined)
  if (typeof page !== 'number') return page;

  // Never return a page number below 1
  if (page < 1) return 1;

  // Never return a page number above the number of pages
  if (page > count) return count;

  // Otherwise, return page.
  return Math.floor(page);
};

/**
 * Handles the pagination logic.
 */
var usePagination = function usePagination(args) {
  var _args$count = args.count,
    countProp = _args$count === void 0 ? 10 : _args$count,
    pageProp = args.page,
    _args$defaultPage = args.defaultPage,
    defaultPage = _args$defaultPage === void 0 ? 1 : _args$defaultPage,
    _args$boundaryCount = args.boundaryCount,
    boundaryCount = _args$boundaryCount === void 0 ? 1 : _args$boundaryCount,
    _args$siblingCount = args.siblingCount,
    siblingCount = _args$siblingCount === void 0 ? 1 : _args$siblingCount,
    _args$getAriaLabel = args.getAriaLabel,
    getAriaLabel = _args$getAriaLabel === void 0 ? defaultGetAriaLabel : _args$getAriaLabel,
    _args$renderItem = args.renderItem,
    renderItem = _args$renderItem === void 0 ? defaultRenderItem : _args$renderItem,
    handleChange = args.onChange,
    isDisabled = args.isDisabled;
  var count = handleCount(countProp);
  var _useControllableState = react.useControllableState({
      defaultValue: handlePage(defaultPage, count),
      value: handlePage(pageProp, count),
      shouldUpdate: function shouldUpdate(prev, next) {
        if (next < 1 || next > count) return false;
        return true;
      }
    }),
    _useControllableState2 = _slicedToArray__default["default"](_useControllableState, 2),
    page = _useControllableState2[0],
    setPage = _useControllableState2[1];
  var _useControllableProp = react.useControllableProp(pageProp, page),
    _useControllableProp2 = _slicedToArray__default["default"](_useControllableProp, 1),
    isControlled = _useControllableProp2[0];
  var handleClick = React.useCallback(function (event, p) {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    if (!isControlled) {
      setPage(p);
    }
    if (handleChange) {
      handleChange(event, p);
    }
  }, [isControlled, setPage, handleChange, isDisabled]);
  var startPages = range(1, Math.min(boundaryCount, count));
  var endPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count);
  var siblingsStart = Math.max(Math.min(
  // Natural start
  page - siblingCount,
  // Lower boundary when page is high
  count - boundaryCount - siblingCount * 2 - 1),
  // Greater than startPages
  boundaryCount + 2);
  var siblingsEnd = Math.min(Math.max(
  // Natural end
  page + siblingCount,
  // Upper boundary when page is low
  boundaryCount + siblingCount * 2 + 2),
  // Less than endPages
  endPages.length > 0 ? endPages[0] - 2 : count - 1);
  var itemList = [].concat(_toConsumableArray__default["default"](startPages), _toConsumableArray__default["default"](siblingsStart > boundaryCount + 2 ? ['start-ellipsis'] : boundaryCount + 1 < count - boundaryCount ? [boundaryCount + 1] : []), _toConsumableArray__default["default"](range(siblingsStart, siblingsEnd)), _toConsumableArray__default["default"](siblingsEnd < count - boundaryCount - 1 ? ['end-ellipsis'] : count - boundaryCount > boundaryCount ? [count - boundaryCount] : []), _toConsumableArray__default["default"](endPages));
  return {
    page: page,
    count: count,
    handleClick: handleClick,
    itemList: itemList,
    getAriaLabel: getAriaLabel,
    renderItem: renderItem,
    isDisabled: isDisabled
  };
};
exports.usePagination = usePagination;